import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {ConversationCommentBox, type ConversationCommentBoxProps} from '@github-ui/diff-lines/conversation-comment-box'
import useSafeState from '@github-ui/use-safe-state'
import {MaximizeIcon, StopIcon, TriangleDownIcon, XIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Dialog, Flash, Heading, IconButton, Stack} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import type {AllowedNonCommentReviewType, PullRequest, Repository} from '../../page-data/payloads/toolbar'
import {PullRequestState} from '../../types'
import {useSubmitReviewMutation, ReviewEvent} from '../../mutations/use-submit-review-mutation'
import type {ReviewResponse} from '../../page-data/payloads/review-response'
import {useAbandonReviewMutation} from '../../mutations/use-abandon-review-mutation'
import {usePersistedReview} from '../../hooks/use-persisted-review-data'
import styles from './ReviewMenuButton.module.css'
import {useGenerateThreadPreviews} from '../../hooks/use-generate-thread-previews'
import type {ThreadPreview as PendingCommentPreviewType} from '../../page-data/payloads/thread-previews'
import {useDiffEntries, usePendingReviews} from '../../stores/selectors'
import {KeybindingHint} from '@primer/react/experimental'
import {ReviewTypeSelector} from './review-menu/ReviewTypeSelector'
import {ReviewCommentsSection} from './review-menu/ReviewCommentsSection'
import {OverlayPortalContainer} from './OverlayPortalContainer'

const REVIEW_MENU_PORTAL_ROOT = 'review-menu-portal-root'

export interface ReviewMenuButtonProps {
  currentUserLogin: string
  pullRequest: PullRequest
  repository: Repository
  redirectOnMutation?: boolean
}

function getReviewMenuButtonDisplayState({
  viewerIsAuthor,
  viewerAllowedNonCommentReviewTypes,
  isPROpen,
}: {
  viewerIsAuthor: boolean
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[]
  isPROpen: boolean
}): {isHidden: boolean; text?: string} {
  const viewerCanApprove = viewerAllowedNonCommentReviewTypes.includes('APPROVE')
  const viewerCanRequestChanges = viewerAllowedNonCommentReviewTypes.includes('REQUEST_CHANGES')

  switch (true) {
    case !isPROpen:
      return {
        isHidden: false,
        text: 'comments',
      }
    case viewerIsAuthor:
    case viewerCanApprove:
    case viewerCanRequestChanges:
      return {
        isHidden: false,
        text: 'review',
      }
    default:
      return {
        isHidden: false,
        text: 'comments',
      }
  }
}

export function ReviewMenuButton({
  currentUserLogin,
  pullRequest,
  repository,
  redirectOnMutation = true,
}: ReviewMenuButtonProps) {
  const {
    author,
    pathName,
    state,
    viewerAllowedNonCommentReviewTypes,
    viewerHasViolatedPushPolicy,
    viewerIsCopilotAttributed,
    comparison,
  } = pullRequest
  const pendingCommentData = usePendingReviews()
  const diffEntriesMap = useDiffEntries()
  const {persistedReview, persistReviewToStorage, removePersistedReviewFromStorage} = usePersistedReview(pathName)

  const commentsList = useGenerateThreadPreviews(pendingCommentData?.pendingReviewIDs, diffEntriesMap)

  const totalPendingComments = commentsList?.length ?? 0

  // We will probably want to use CurrentUserContext and useCurrentUser
  const viewerIsAuthor = author?.login === currentUserLogin
  const isPROpen = state !== PullRequestState.Closed && state !== PullRequestState.Merged
  const viewerCanWriteToRepo = repository.viewerPermission === 'WRITE' || repository.viewerPermission === 'ADMIN'
  const headRefOid = comparison.headOid
  const [openReviewMenu, setOpenReviewMenu] = useSafeState<'dialog' | 'anchored' | 'none'>('none')
  const [reviewSubmissionState, setReviewSubmissionState] = useSafeState<'idle' | 'submitting' | 'canceling'>('idle')
  const [errorMessage, setErrorMessage] = useSafeState<string | undefined>()
  const [reviewEvent, setReviewEvent] = useSafeState<ReviewEvent>(persistedReview?.event ?? ReviewEvent.comment)

  const hasPendingReviews = (pendingCommentData?.pendingReviewIDs ?? []).length > 0
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  // ref and state to keep track of the review body without causing re-renders
  // we ultimately only care about the final value when submitting the review and validating review body presence
  const initialReviewBody = persistedReview?.text ?? ''
  const reviewBodyRef = useRef<string>(initialReviewBody)
  const [hasReviewBody, setHasReviewBody] = useSafeState<boolean>(!!persistedReview?.text)

  const submitDisabled =
    reviewSubmissionState === 'submitting' ||
    (!hasReviewBody && reviewEvent === ReviewEvent.comment && !totalPendingComments)

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const onReviewBodyChange = useCallback(
    (body: string) => {
      reviewBodyRef.current = body
      setHasReviewBody(!!body.trim())
      persistReviewToStorage(reviewEvent, body)
    },
    [persistReviewToStorage, reviewEvent, setHasReviewBody],
  )

  const handleReviewEventChange = useCallback(
    (newReviewEvent: string | null) => {
      if (Object.values(ReviewEvent).includes(newReviewEvent as ReviewEvent)) {
        setReviewEvent(newReviewEvent as ReviewEvent)
        persistReviewToStorage(newReviewEvent as ReviewEvent, reviewBodyRef.current)
      }
    },
    [persistReviewToStorage, setReviewEvent],
  )

  const {mutate: submitReview} = useSubmitReviewMutation({
    onSuccess: ({redirectUrl}: ReviewResponse) => {
      removePersistedReviewFromStorage()

      if (redirectOnMutation) window.location.href = redirectUrl
    },
    onError: (error: Error) => {
      setReviewSubmissionState('idle')

      // TODO: add better error handling responses here based on message from server
      setErrorMessage(error.message)
    },
  })

  const {mutate: abandonReview} = useAbandonReviewMutation({
    onSuccess: ({redirectUrl}: ReviewResponse) => {
      removePersistedReviewFromStorage()
      if (redirectOnMutation) window.location.href = redirectUrl
    },
    onError: (error: Error) => {
      setReviewSubmissionState('idle')

      // TODO: add better error handling responses here based on message from server
      setErrorMessage(error.message)
    },
  })

  const handleReviewSubmit = useCallback(() => {
    if (submitDisabled) return

    if (errorMessage) setErrorMessage(undefined)
    setReviewSubmissionState('submitting')
    sendPullRequestAnalyticsEvent('submit_review_dialog.submit', 'SUBMIT_REVIEW_BUTTON')
    submitReview({
      body: reviewBodyRef.current,
      event: reviewEvent,
      headSha: headRefOid,
    })
  }, [
    errorMessage,
    headRefOid,
    reviewEvent,
    sendPullRequestAnalyticsEvent,
    setErrorMessage,
    setReviewSubmissionState,
    submitDisabled,
    submitReview,
  ])

  const handleReviewCancel = useCallback(() => {
    if ((pendingCommentData?.pendingReviewIDs ?? []).length === 0) return
    if (!confirm('Are you sure you want to cancel? You will lose all your pending comments.')) return

    if (errorMessage) setErrorMessage(undefined)

    setReviewSubmissionState('canceling')
    sendPullRequestAnalyticsEvent('submit_review_dialog.cancel', 'CANCEL_REVIEW_BUTTON')
    abandonReview()
  }, [
    abandonReview,
    errorMessage,
    pendingCommentData?.pendingReviewIDs,
    sendPullRequestAnalyticsEvent,
    setErrorMessage,
    setReviewSubmissionState,
  ])

  const reviewMenuButtonDisplayState = useMemo(
    () =>
      getReviewMenuButtonDisplayState({
        isPROpen,
        viewerAllowedNonCommentReviewTypes,
        viewerIsAuthor,
      }),
    [viewerAllowedNonCommentReviewTypes, viewerIsAuthor, isPROpen],
  )

  const handleOpenReviewDialog = useCallback(() => {
    // on narrow screens, the dialog is a better experience than the anchored overlay
    if (window.innerWidth < 768) {
      setOpenReviewMenu('dialog')
    } else {
      setOpenReviewMenu('anchored')
    }

    sendPullRequestAnalyticsEvent('submit_review_dialog.open', 'REVIEW_CHANGES_BUTTON')
  }, [sendPullRequestAnalyticsEvent, setOpenReviewMenu])

  const handleCloseReviewDialog = useCallback(() => {
    setOpenReviewMenu('none')
  }, [setOpenReviewMenu])

  const handleNavigateToDiffComment = useCallback(() => {
    setOpenReviewMenu('none')
  }, [setOpenReviewMenu])

  const handleToggleReviewDialog = useCallback(() => {
    if (openReviewMenu === 'none') {
      handleOpenReviewDialog()
    } else {
      handleCloseReviewDialog()
    }
  }, [handleCloseReviewDialog, handleOpenReviewDialog, openReviewMenu])

  const ReviewMenuBody = useMemo(() => {
    return (
      <ReviewMenuBodyComponent
        initialReviewBody={initialReviewBody}
        isPROpen={isPROpen}
        onReviewBodyChange={onReviewBodyChange}
        reviewEvent={reviewEvent}
        viewerAllowedNonCommentReviewTypes={viewerAllowedNonCommentReviewTypes}
        viewerCanWriteToRepo={viewerCanWriteToRepo}
        viewerHasViolatedPushPolicy={viewerHasViolatedPushPolicy}
        viewerIsAuthor={viewerIsAuthor}
        viewerIsCopilotAttributed={viewerIsCopilotAttributed}
        commentsList={commentsList}
        totalPendingComments={totalPendingComments}
        handleNavigateToDiffComment={handleNavigateToDiffComment}
        handleReviewSubmit={handleReviewSubmit}
        handleReviewEventChange={handleReviewEventChange}
      />
    )
  }, [
    commentsList,
    handleNavigateToDiffComment,
    handleReviewEventChange,
    handleReviewSubmit,
    initialReviewBody,
    isPROpen,
    onReviewBodyChange,
    reviewEvent,
    totalPendingComments,
    viewerAllowedNonCommentReviewTypes,
    viewerCanWriteToRepo,
    viewerHasViolatedPushPolicy,
    viewerIsAuthor,
    viewerIsCopilotAttributed,
  ])

  if (reviewMenuButtonDisplayState.isHidden) return null

  const ReviewMenuFooter = () => {
    return (
      <ReviewMenuFooterComponent
        errorMessage={errorMessage}
        hasPendingReviews={hasPendingReviews}
        reviewSubmissionState={reviewSubmissionState}
        handleReviewCancel={handleReviewCancel}
        handleCloseReviewDialog={handleCloseReviewDialog}
        handleReviewSubmit={handleReviewSubmit}
        submitDisabled={submitDisabled}
        reviewMenuButtonDisplayState={reviewMenuButtonDisplayState}
      />
    )
  }

  return (
    <>
      <Button
        count={totalPendingComments ? totalPendingComments : undefined}
        className={styles.ReviewMenuButton}
        trailingAction={TriangleDownIcon}
        variant="primary"
        onClick={handleToggleReviewDialog}
        size="small"
        ref={submitButtonRef}
      >
        Submit {reviewMenuButtonDisplayState.text}
      </Button>

      <OverlayPortalContainer portalName={REVIEW_MENU_PORTAL_ROOT} />

      <AnchoredOverlay
        open={openReviewMenu === 'anchored'}
        anchorRef={submitButtonRef}
        width="xlarge"
        onClose={handleCloseReviewDialog}
        displayCloseButton={false}
        variant={{regular: 'anchored', narrow: 'fullscreen'}}
        renderAnchor={null}
        focusZoneSettings={{disabled: true}}
        overlayProps={{
          role: 'dialog',
          'aria-labelledby': 'anchored-review-title',
          'aria-modal': 'true',
          portalContainerName: REVIEW_MENU_PORTAL_ROOT,
        }}
      >
        <Stack direction="horizontal" align="center" justify="space-between" className="py-2 border-bottom pl-3 pr-2">
          <Heading as="h1" className="flex-1 f5" variant="small" id="anchored-review-title">
            Finish your {reviewMenuButtonDisplayState.text}
          </Heading>
          <Stack direction="horizontal" align="center" gap="none">
            {hasPendingReviews && (
              <IconButton
                icon={MaximizeIcon}
                className={styles.ExpandSection}
                variant="invisible"
                aria-label="Expand this section"
                onClick={() => setOpenReviewMenu('dialog')}
              />
            )}
            <IconButton icon={XIcon} variant="invisible" aria-label="Close" onClick={handleCloseReviewDialog} />
          </Stack>
        </Stack>

        <div className={styles.AnchoredReviewBody}>{ReviewMenuBody}</div>

        <div className="p-3 border-top">
          {/* eslint-disable-next-line react-hooks/static-components */}
          <ReviewMenuFooter />
        </div>
      </AnchoredOverlay>

      {openReviewMenu === 'dialog' && (
        <Dialog
          onClose={handleCloseReviewDialog}
          returnFocusRef={submitButtonRef}
          aria-label="Review changes"
          position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
          title={`Finish your ${reviewMenuButtonDisplayState.text}`}
          renderFooter={() => (
            <Dialog.Footer>
              <ReviewMenuFooter />
            </Dialog.Footer>
          )}
        >
          {ReviewMenuBody}
        </Dialog>
      )}
    </>
  )
}

/**
 * ControlledCommentBox is a wrapper around ConversationCommentBox that allows
 * it to be controlled by an initial value and an onChange callback.
 * This is useful for cases where you want to manage the comment box state
 * outside of the component to prevent unnecessary re-renders on every keystroke.
 */
const ControlledCommentBox = ({
  ref,
  initialValue,
  onChange,
  ...props
}: Omit<ConversationCommentBoxProps, 'value'> & {
  initialValue: string
  onChange: (value: string) => void
} & {ref?: React.Ref<CommentBoxHandle>}) => {
  const [reviewBody, setReviewBody] = useSafeState<string>(initialValue)

  const onCommentBoxChange = useCallback(
    (value: string) => {
      setReviewBody(value)
      onChange(value)
    },
    [setReviewBody, onChange],
  )

  return <ConversationCommentBox {...props} value={reviewBody} onChange={onCommentBoxChange} ref={ref} />
}

ControlledCommentBox.displayName = 'ControlledCommentBox'

type ReviewMenuFooterProps = {
  errorMessage?: string
  handleReviewCancel: () => void
  handleCloseReviewDialog: () => void
  handleReviewSubmit: () => void
  hasPendingReviews: boolean
  submitDisabled?: boolean
  reviewMenuButtonDisplayState: {text?: string}
  reviewSubmissionState: 'idle' | 'submitting' | 'canceling'
}

function ReviewMenuFooterComponent({
  errorMessage,
  handleReviewCancel,
  handleCloseReviewDialog,
  handleReviewSubmit,
  hasPendingReviews,
  submitDisabled,
  reviewMenuButtonDisplayState,
  reviewSubmissionState,
}: ReviewMenuFooterProps) {
  const isCancelling = reviewSubmissionState === 'canceling'
  const isSubmitting = reviewSubmissionState === 'submitting'

  return (
    <>
      {errorMessage && (
        <Flash className={styles.ErrorMessage} variant="danger">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
      <div className="d-flex flex-row flex-1 flex-items-center flex-justify-between gap-2">
        {hasPendingReviews ? (
          <Button
            disabled={isCancelling || isSubmitting}
            loading={isCancelling}
            onClick={handleReviewCancel}
            tabIndex={0}
            variant="danger"
          >
            <Stack direction="horizontal" align="center">
              Discard {reviewMenuButtonDisplayState.text}
            </Stack>
          </Button>
        ) : (
          <div />
        )}
        <div className="d-flex flex-row gap-2">
          <Button onClick={handleCloseReviewDialog} disabled={isCancelling || isSubmitting}>
            Cancel
          </Button>
          <Button
            disabled={submitDisabled || isSubmitting}
            variant="primary"
            onClick={handleReviewSubmit}
            loading={isSubmitting}
            className={styles.SubmitReviewButton}
            trailingVisual={<KeybindingHint keys="Mod+enter" />}
          >
            <div className="d-flex flex-row flex-justify-center">Submit {reviewMenuButtonDisplayState.text}</div>
          </Button>
        </div>
      </div>
    </>
  )
}

type ReviewMenuBodyProps = {
  initialReviewBody: string
  isPROpen: boolean
  onReviewBodyChange: (body: string) => void
  reviewEvent: ReviewEvent
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[]
  viewerCanWriteToRepo: boolean
  viewerHasViolatedPushPolicy?: boolean | null
  viewerIsAuthor: boolean
  viewerIsCopilotAttributed: boolean
  tabSize?: number
  commentsList?: PendingCommentPreviewType[]
  totalPendingComments?: number
  handleNavigateToDiffComment: () => void
  handleReviewSubmit: () => void
  handleReviewEventChange: (newReviewEvent: string | null) => void
}

function ReviewMenuBodyComponent({
  initialReviewBody,
  isPROpen,
  onReviewBodyChange,
  reviewEvent,
  viewerAllowedNonCommentReviewTypes,
  viewerCanWriteToRepo,
  viewerHasViolatedPushPolicy,
  viewerIsAuthor,
  viewerIsCopilotAttributed,
  commentsList,
  totalPendingComments,
  handleNavigateToDiffComment,
  handleReviewSubmit,
  handleReviewEventChange,
}: ReviewMenuBodyProps) {
  const markdownInputRef = useRef<CommentBoxHandle>(null)

  useEffect(() => {
    const timeout = window.setTimeout(() => markdownInputRef.current?.focus())
    return () => {
      window.clearTimeout(timeout)
    }
  }, [])

  return (
    <>
      <ControlledCommentBox
        ref={markdownInputRef}
        label="Add review comment"
        placeholder="Leave a comment"
        className={styles.CommentBoxContainer}
        initialValue={initialReviewBody}
        onChange={onReviewBodyChange}
        onPrimaryAction={handleReviewSubmit}
      />

      <ReviewTypeSelector
        isPROpen={isPROpen}
        reviewEvent={reviewEvent}
        viewerAllowedNonCommentReviewTypes={viewerAllowedNonCommentReviewTypes}
        viewerCanWriteToRepo={viewerCanWriteToRepo}
        viewerHasViolatedPushPolicy={viewerHasViolatedPushPolicy}
        viewerIsAuthor={viewerIsAuthor}
        viewerIsCopilotAttributed={viewerIsCopilotAttributed}
        handleReviewEventChange={handleReviewEventChange}
        handleReviewSubmit={handleReviewSubmit}
      />

      <ReviewCommentsSection
        totalPendingComments={totalPendingComments}
        commentsList={commentsList}
        handleNavigateToDiffComment={handleNavigateToDiffComment}
      />
    </>
  )
}

try{ ReviewMenuButton.displayName ||= 'ReviewMenuButton' } catch {}
try{ ReviewMenuBody.displayName ||= 'ReviewMenuBody' } catch {}
try{ ReviewMenuFooter.displayName ||= 'ReviewMenuFooter' } catch {}
try{ ReviewMenuFooterComponent.displayName ||= 'ReviewMenuFooterComponent' } catch {}
try{ ReviewMenuBodyComponent.displayName ||= 'ReviewMenuBodyComponent' } catch {}